import { finiteOrNull, harmonicMean, mean, median, sd } from './stats';
import type { BiasScores, CellStats, ScoredTrial } from './types';

function cell(rts: readonly number[]): CellStats {
  return { n: rts.length, mean: mean(rts), harmonicMean: harmonicMean(rts), median: median(rts), sd: finiteOrNull(sd(rts)) };
}
const rtsOf = (ts: readonly ScoredTrial[]): number[] => ts.map((t) => t.rt as number);

/**
 * 편향 점수. 부호 규약은 문헌 표준 — 양수 = 위협 경계(vigilance).
 *
 *   BS = M(RT_incongruent) − M(RT_congruent)
 *      = M(탐침@중립위치) − M(탐침@위협위치)
 *
 * 1차 지표는 4셀 조화평균 (Hazen 2009 / Pond 2025):
 *   AB = ((TUPL − TUPU) + (TLPU − TLPL)) / 2
 *   TU/TL = 위협 위/아래, PU/PL = 탐침 위/아래.  수평 배치면 left/right 로 같은 구조.
 */
export function computeBiasScores(valid: readonly ScoredTrial[]): BiasScores {
  const tn = valid.filter((t) => t.trialType === 'threat-neutral' && t.threatSide !== null);
  const cong = tn.filter((t) => t.congruency === 'congruent');
  const inc = tn.filter((t) => t.congruency === 'incongruent');
  const neu = valid.filter((t) => t.trialType === 'neutral-neutral');

  // 셀 키: `${threatSide}/${probeSide}`
  const cells: Record<string, CellStats> = {};
  const groups = new Map<string, number[]>();
  for (const t of tn) {
    const k = `${t.threatSide}/${t.probeSide}`;
    const g = groups.get(k) ?? [];
    g.push(t.rt as number);
    groups.set(k, g);
  }
  for (const [k, v] of groups) cells[k] = cell(v);

  // 두 위치 라벨을 데이터에서 읽는다 (top/bottom 또는 left/right)
  const sides = [...new Set(tn.map((t) => t.threatSide as string))].sort();
  let fourCell: number | null = null;
  let lowerOnly: number | null = null;
  if (sides.length === 2) {
    const [A, B] = sides as [string, string];      // 'bottom','top' 또는 'left','right'
    const TApA = cells[`${A}/${A}`], TApB = cells[`${A}/${B}`], TBpA = cells[`${B}/${A}`], TBpB = cells[`${B}/${B}`];
    if (TApA && TApB && TBpA && TBpB) {
      // 위협 A, 탐침 B (incongruent) − 위협 A, 탐침 A (congruent)  +  위협 B, 탐침 A (incongruent) − 위협 B, 탐침 B (congruent)
      fourCell = ((TApB.harmonicMean - TApA.harmonicMean) + (TBpA.harmonicMean - TBpB.harmonicMean)) / 2;
    }
    // 아래쪽 탐침만: 수직이면 'bottom'. 탐침@bottom 에서 위협@top(incongruent) − 위협@bottom(congruent)
    const lower = sides.includes('bottom') ? 'bottom' : null;
    if (lower) {
      const upper = lower === A ? B : A;
      const incL = cells[`${upper}/${lower}`], conL = cells[`${lower}/${lower}`];
      if (incL && conL) lowerOnly = incL.harmonicMean - conL.harmonicMean;
    }
  }

  const c = cong.length ? cell(rtsOf(cong)) : null;
  const i = inc.length ? cell(rtsOf(inc)) : null;
  const simple = c && i ? i.mean - c.mean : null;
  const se = c && i && c.sd !== null && i.sd !== null ? Math.sqrt(c.sd ** 2 / c.n + i.sd ** 2 / i.n) : null;

  const half = (pred: (t: ScoredTrial) => boolean): number | null => {
    const hc = cong.filter(pred), hi = inc.filter(pred);
    return hc.length && hi.length ? finiteOrNull(mean(rtsOf(hi)) - mean(rtsOf(hc))) : null;
  };

  return {
    signConvention: 'vigilance-positive',
    fourCellHarmonic: finiteOrNull(fourCell ?? NaN),
    simpleMean: simple === null ? null : finiteOrNull(simple),
    simpleMeanSe: se === null ? null : finiteOrNull(se),
    lowerProbeOnly: finiteOrNull(lowerOnly ?? NaN),
    cells,
    congruent: c, incongruent: i,
    neutral: neu.length ? cell(rtsOf(neu)) : null,
    halves: { odd: half((t) => t.trialIndex % 2 === 0), even: half((t) => t.trialIndex % 2 === 1) },
  };
}
